/**
 * Число римскими цифрами
 * ====================================
 * TODO Ключевые слова:
 *   - века римскими цифрами - https://obrazovaka.ru/question/veka-tablica-s-perevodom-117498
 *   - года римскими цифрами
 *   - римские цифры +до 20	12 821
 *   - римские цифры +от 1 +до 20
 *   - римские цифры +на клавиатуре
 *   - римские цифры +на телефоне
 *   - Регулярное выражение с римскими цифрами?
 */
import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { Link, useLoaderData, useLocation } from "react-router";
import { FormEvent } from "react";
import { useTranslation } from "react-i18next";

import Button from "~/components/Button";
import NumberInput from "~/components/forms/NumberInput";
import H2 from "~/components/H2";
import Icon from "~/components/Icon";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import ToolHeader from "~/components/tool-page/ToolHeader";
import { arabicToRoman } from "~/helpers/numbers/roman-numbers";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import Author from "~/components/tool-page/Author";
import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";
import { getBaseUrl } from "~/helpers/getBaseUrl";

const CATEGORY = "numbers";
const TKEY = "calculators.numbers.number_to_roman";
const ROUTE_ENDING = "number-to-roman";
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");

function getOrdinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov
    )
  ];
  addNoindexMeta(meta, data.meta.shouldIndex);
  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }
  return meta;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);

  const locale = getLocale(request.url);


  // Calculate result if number is set
  let error: string | null = null;
  let romanNumber: string | null = null;

  if (Number.isNaN(number)) {
    throw new Response(null, { status: 404 });
  }

  try {
    if (number !== null) {
      if (!Number.isInteger(number) || number <= 0 || number > 99999999) {
        error = t(`${TKEY}.error_invalid_number`);
      } else {
        romanNumber = arabicToRoman(number);
      }

    }
  } catch (e) {
    error = t("error_unknown");
  }

  const title = number
    ? t(`${TKEY}.title_for_number`, { number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  let shouldIndex: ShouldIndexMeta = {
    googlebot: number === null,
    yandex: false,
    bingbot: false,
  };

  if (
    number === null
    || (Number.isInteger(number) && number >= 1 && number <= 1000)
  ) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  }

  return {
    number,
    romanNumber,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, romanNumber })
        : t(`${TKEY}.description`),
      breadcrumbs,
      shouldIndex,
      canonical: getBaseUrl() + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING,
    }
  };
};

function calculate(e: FormEvent<HTMLFormElement>) {
  e.preventDefault();

  const formData = new FormData(e.currentTarget);

  const number = parseInt(formData.get("number") as string, 10);

  // Analyze the current URL and replace or add the slug accordingly
  let currentPathname = window.location.pathname;
  const slugPattern = new RegExp(`/${ROUTE_ENDING}/?(.*)$`);

  if (slugPattern.test(currentPathname)) {
    // Replace existing slug
    currentPathname = currentPathname.replace(slugPattern, `/${ROUTE_ENDING}/${number}`);
  } else {
    // Add slug to the pathname
    currentPathname = currentPathname.endsWith("/") ? `/${ROUTE_ENDING}/${number}` : `/${ROUTE_ENDING}/${number}/`;
  }

  // Redirect to the new path if it changed
  const newUrl = window.location.origin + currentPathname;
  if (window.location.href === newUrl) {
    return;
  }

  window.location.href = newUrl;
}

export default function Index() {
  const {
    meta,
    number,
    romanNumber,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const currentYear = new Date().getFullYear();

  const commonRomanNumbers = {
    "1": "I",
    "2": "II",
    "3": "III",
    "4": "IV",
    "5": "V",
    "6": "VI",
    "7": "VII",
    "8": "VIII",
    "9": "IX",
    "10": "X",
    "20": "XX",
    "30": "XXX",
    "40": "XL",
    "50": "L",
    "60": "LX",
    "70": "LXX",
    "80": "LXXX",
    "90": "XC",
    "100": "C",
    "200": "CC",
    "300": "CCC",
    "400": "CD",
    "500": "D",
    "600": "DC",
    "700": "DCC",
    "800": "DCCC",
    "900": "CM",
    "1000": "M",
    "2000": "MM",
    "3000": "MMM",
    "4000": "M̅V",
    "5000": "V̅",
    "6000": "V̅M",
    "7000": "V̅MM",
    "8000": "V̅MMM",
    "9000": "M̅X",
    "10000": "X̅",
    "20000": "X̅X̅",
    "30000": "X̅X̅X̅",
    "40000": "X̅L̅",
    "50000": "L̅",
    "60000": "L̅X̅",
    "70000": "L̅X̅X̅",
    "80000": "L̅X̅X̅X̅",
    "90000": "X̅C̅",
    "100000": "C̅",
    "200000": "C̅C̅",
    "300000": "C̅C̅C̅",
    "400000": "C̅D̅",
    "500000": "D̅",
    "600000": "D̅C̅",
    "700000": "D̅C̅C̅",
    "800000": "D̅C̅C̅C̅",
    "900000": "C̅M̅",
    "1000000": "M̅",
    "2000000": "M̅M̅",
    "3000000": "M̅M̅M̅",
    "4000000": "I̅̅V or M̅̅V",
    "5000000": "V̅̅",
    "6000000": "V̅̅M",
    "7000000": "V̅̅MM",
    "8000000": "V̅̅MMM",
    "9000000": "I̅̅X or M̅̅X",
    "10000000": "X̅̅"
  };

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.title}
          description={meta.description}
        />

        {romanNumber && !error ? <Result color="positive">
          <H2 className="first-letter:capitalize">{t("answer")}:</H2>

          <p className="mt-8 text-3xl font-bold first-letter:capitalize font-serif">
            {romanNumber}
          </p>
        </Result> : null}

        <ToolForm onSubmit={calculate}>
          <span className="font-semibold">{t(`${TKEY}.question`)}</span>
          <div className="w-[120px]">
            <NumberInput
              name="number"
              defaultValue={number ?? 30}
              min={1}
              max={99999999}
              ariaLabel={t(`${TKEY}.input_number`)}
            />
          </div>


          <Button className="w-full" isSubmit={true}>
            <Icon v="keypad" className="pr-2" /> {t("do_convert")}
          </Button>
        </ToolForm>

        {error ? (
            <Result color="negative">
              <p>{error}</p>
            </Result>
          )
          : null
        }
      </CalculatorLayout>

      <AdditionalContentLayout>
        {number !== null ? null : (
          <AdditionalInfo>
            <AdditionalInfoSection
              id="why"
              contentsText={t(`${TKEY}.info_h_roman_metric_system`)}
              contentsEmoji="❓"
            >
              <H2>{t(`${TKEY}.info_h_roman_metric_system`)}</H2>
              <p>{t(`${TKEY}.info_p1_roman_metric_system`)}</p>
              <p>{t(`${TKEY}.info_p2_roman_metric_system`)}</p>
              <p>{t(`${TKEY}.info_p3_roman_metric_system`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="about"
              contentsText={t(`${TKEY}.info_h_meaning_of_numerals`)}
              contentsEmoji="🔣"
            >
              <H2>{t(`${TKEY}.info_h_meaning_of_numerals`)}</H2>
              <p>{t(`${TKEY}.info_h_meaning_of_numerals_p1`)}</p>
              <ul className="flex flex-wrap justify-center gap-4 text-gray-700 font-bold">
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">I&nbsp;= 1</li>
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">V&nbsp;= 5</li>
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">X&nbsp;= 10</li>
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">L&nbsp;= 50</li>
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">C&nbsp;= 100</li>
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">D&nbsp;= 500</li>
                <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">M&nbsp;= 1000</li>
              </ul>
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="how-to-write"
              contentsText={t(`${TKEY}.info_h_how_to_write`)}
              contentsEmoji="✍️"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write`)}</H2>
              <ol className="list-decimal pl-4 flex flex-col gap-8">
                <li>
                  <p>{t(`${TKEY}.info_h_how_to_write_p1`)}</p>
                  <ul className="flex flex-wrap justify-center gap-4 pb-4 text-gray-700 font-bold">
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">V̅&nbsp;=&nbsp;5 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">X̅&nbsp;=&nbsp;10 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">L̅&nbsp;=&nbsp;50 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">C̅&nbsp;=&nbsp;100 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">D̅&nbsp;=&nbsp;500 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">M̅&nbsp;=&nbsp;1 000 000</li>
                  </ul>

                  <p>{t(`${TKEY}.info_h_how_to_write_p2`)}</p>
                  <ul className="flex flex-wrap justify-center gap-4 text-gray-700 font-bold">
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">V̅̅&nbsp;=&nbsp;5 000 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">X̅̅&nbsp;=&nbsp;10 000 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">L̅̅&nbsp;=&nbsp;50 000 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">C̅̅&nbsp;=&nbsp;100 000 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">C̅̅L̅̅&nbsp;=&nbsp;500 000 000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">M̅̅&nbsp;=&nbsp;1 000 000 000</li>
                  </ul>
                </li>

                <li>
                  <p>{t(`${TKEY}.info_h_how_to_write_p3`)}</p>
                  <p>{t(`${TKEY}.info_h_how_to_write_p4`)}</p>
                  <ul className="flex flex-wrap justify-center gap-4 pb-4 text-gray-700 font-bold">
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2 text-green-700">XIII&nbsp;=&nbsp;13</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2 text-green-700">LXX&nbsp;=&nbsp;70</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2 text-green-700">MCCC&nbsp;=&nbsp;1300</li>
                  </ul>

                  <p>{t(`${TKEY}.info_h_how_to_write_p5`)}</p>
                  <ul className="flex flex-wrap justify-center gap-4 text-gray-700 font-bold">
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2 text-red-900">MMMM&nbsp;=&nbsp;4000</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2 text-red-900">VVX&nbsp;=&nbsp;20</li>
                  </ul>
                </li>

                <li>
                  <p>{t(`${TKEY}.info_h_how_to_write_p6`)}</p>
                  <ul className="flex flex-wrap justify-center gap-4 text-gray-700 font-bold">
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">VII&nbsp;= 5 + 2 =&nbsp;7</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">XVI&nbsp;= 10 + 5 + 1 =&nbsp;16</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">DX&nbsp;= 500 + 10 =&nbsp;510</li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">MDD&nbsp;= 1000 + 100 + 100 =&nbsp;1200
                    </li>
                  </ul>
                </li>

                <li>
                  <p>{t(`${TKEY}.info_h_how_to_write_p7`)}</p>
                  <ul className="flex flex-wrap justify-center gap-4 text-gray-700 font-bold">
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">IV&nbsp;= 5 - 1 =&nbsp;<strong>4</strong>
                    </li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">IX&nbsp;= 10 - 1 =&nbsp;
                      <strong>9</strong>
                    </li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">XL&nbsp;= 50 - 10 =&nbsp;
                      <strong>40</strong>
                    </li>
                    <li className="bg-gray-200 rounded-xs shadow-xs px-3 py-2">XC&nbsp;= 100 - 10 =&nbsp;
                      <strong>90</strong>
                    </li>
                  </ul>
                </li>
              </ol>
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="zero"
              contentsText={t(`${TKEY}.info_h_how_to_write_zero`)}
              contentsEmoji="0️⃣"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write_zero`)}</H2>
              <p>{t(`${TKEY}.info_h_how_to_write_zero_p1`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="current-year"
              contentsText={t(`${TKEY}.info_h_how_to_write_current_year`, { year: currentYear })}
              contentsEmoji="📅"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write_current_year`, { year: currentYear })}</H2>
              <p>{t(`${TKEY}.info_p_how_to_write_current_year`, {
                year: currentYear,
                yearRoman: arabicToRoman(currentYear),
                explanation: `${arabicToRoman(2000)} (2000) + `
                  + `${arabicToRoman(Math.floor(currentYear % 100 / 10) * 10)} (${Math.floor(currentYear % 100 / 10) * 10}) + `
                  + (currentYear % 10 === 0 ? "" : `${arabicToRoman(currentYear % 10)} (${currentYear % 10})`)
              })}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="21-century"
              contentsText={t(`${TKEY}.info_h_how_to_write_21_century`, { century: 21, ordinalSuffix: getOrdinalSuffix(21) })}
              contentsEmoji="🌟"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write_21_century`, { century: 21, ordinalSuffix: getOrdinalSuffix(21) })}</H2>
              <p dangerouslySetInnerHTML={{
                __html: t(`${TKEY}.info_p_how_to_write_21_century`, {
                  century: 21,
                  ordinalSuffix: getOrdinalSuffix(21),
                  centuryRoman: arabicToRoman(21),
                  explanation: "XX (20) + I (1)"
                })
              }} />
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="20-century"
              contentsText={t(`${TKEY}.info_h_how_to_write_20_century`, { century: 20, ordinalSuffix: getOrdinalSuffix(20) })}
              contentsEmoji="🗓️"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write_20_century`, { century: 20, ordinalSuffix: getOrdinalSuffix(20) })}</H2>
              <p dangerouslySetInnerHTML={{
                __html: t(`${TKEY}.info_p_how_to_write_20_century`, {
                  century: 20,
                  ordinalSuffix: getOrdinalSuffix(20),
                  centuryRoman: arabicToRoman(20),
                  explanation: "XX (20)"
                })
              }} />
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="19-century"
              contentsText={t(`${TKEY}.info_h_how_to_write_19_century`, { century: 19, ordinalSuffix: getOrdinalSuffix(19) })}
              contentsEmoji="⏮️"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write_19_century`, { century: 19, ordinalSuffix: getOrdinalSuffix(19) })}</H2>
              <p dangerouslySetInnerHTML={{
                __html: t(`${TKEY}.info_p_how_to_write_19_century`, {
                  century: 19,
                  ordinalSuffix: getOrdinalSuffix(19),
                  centuryRoman: arabicToRoman(19),
                  explanation: "X (10) + IX (9)"
                })
              }} />
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="other-centuries"
              contentsText={t(`${TKEY}.info_h_how_to_write_other_centuries`)}
              contentsEmoji="⏪"
            >
              <H2>{t(`${TKEY}.info_h_how_to_write_other_centuries`)}</H2>
              <p>{t(`${TKEY}.info_p_how_to_write_other_centuries`)}</p>
              <div className="columns-2 md:columns-3 gap-12">
                <table className="table-auto">
                  <tbody>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].reverse().map((century) => (
                    <tr key={century} className="gap-2">
                      <td className="pr-3 text-right">
                        <Link
                          to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/${century}`}>{t(`${TKEY}.century`, { century: century, ordinalSuffix: getOrdinalSuffix(century) })}</Link>
                      </td>
                      <td>{arabicToRoman(century)}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </AdditionalInfoSection>

            <AdditionalInfoSection
              id="common-numbers"
              contentsText={t(`${TKEY}.info_h_main_roman_numbers`)}
              contentsEmoji="🔢"
            >
              <H2>{t(`${TKEY}.info_h_main_roman_numbers`)}</H2>
              <div className="columns-2 md:columns-3 gap-12">
                <table className="table-auto">
                  <tbody>
                  {Object.entries(commonRomanNumbers).map(([arabic, roman]) => (
                    <tr key={arabic} className="gap-2">
                      <td className="pr-3 text-right"><Link
                        to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/${arabic}`}>{arabic}</Link></td>
                      <td>{roman}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </AdditionalInfoSection>
          </AdditionalInfo>
        )}

        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>
    </>
  );
}


